/**
 * computePipeGeometry — pure helpers for deriving pipe length and
 * inclination from geo coordinates + elevations.
 *
 * Shared by:
 *   - Feature A (store `onConnect`): a straight 2-endpoint pipe whose
 *     length = haversine between the two component coordinates.
 *   - Feature B (`fromKmz.ts`): a multi-vertex pipeline polyline whose
 *     length = summed haversine over all segments.
 *
 * Sign convention (CLAUDE.md: "Document every change")
 * ----------------------------------------------------
 *   inclinationRad = asin(clamp(Δz / length, -1, 1))
 *   where Δz = elevationDownstream − elevationUpstream (metres ASL).
 *
 *   +inclination  ⇒ flow goes UPHILL (downstream higher than upstream)
 *   −inclination  ⇒ flow goes DOWNHILL
 *    0            ⇒ flat (Δz == 0) or undefined length
 *
 * This matches the momentum solver's gravity term
 * (`momentumSolver.ts`: uses `Math.sin(inclination)`), where a positive
 * inclination subtracts static head as flow climbs.
 *
 * Units
 * -----
 *   - coordinates: WGS84 degrees ({lat, lng}).
 *   - elevations:  metres ASL.
 *   - length:      metres.
 *   - inclination: radians from horizontal.
 *
 * Pure: no React, no Zustand, no I/O. Reuses `haversineMeters` from
 * `siteLayersBlock.ts` (single numerically-stable implementation).
 */

import type { LatLng } from '../../../geometry/sampleGrid';
import { haversineMeters } from '../../../../services/siteLayersBlock';

/** Smallest pipe length (m) below which inclination is treated as flat. */
export const MIN_PIPE_LENGTH_M = 1e-6;

/**
 * Cumulative length of a polyline in metres — sum of consecutive
 * haversine segments. Returns 0 for fewer than two vertices.
 *
 * Multi-segment safe: every consecutive pair contributes, so a
 * multi-vertex pipeline keeps its full routed length (guards against
 * collapsing a polyline to its endpoint chord).
 */
export function polylineLengthMeters(coords: readonly LatLng[]): number {
  if (!Array.isArray(coords) || coords.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < coords.length; i++) {
    sum += haversineMeters(coords[i - 1], coords[i]);
  }
  return sum;
}

/**
 * Inclination in radians from a length and the elevation drop along the
 * flow direction.
 *
 *   inclinationRad = asin(clamp(Δz / length, -1, 1))
 *
 * Guards:
 *   - length ≤ MIN_PIPE_LENGTH_M ⇒ 0 (avoid div-by-zero / NaN).
 *   - non-finite Δz or length    ⇒ 0.
 *   - |Δz| > length              ⇒ clamped to ±1 before asin (a
 *     physically impossible vertical drop, e.g. from noisy elevation
 *     data, can never push asin out of domain).
 *
 * @param elevationUpstream    metres ASL at the flow inlet.
 * @param elevationDownstream  metres ASL at the flow outlet.
 * @param lengthMeters         pipe length in metres.
 */
export function inclinationRadFromElevations(
  elevationUpstream: number,
  elevationDownstream: number,
  lengthMeters: number,
): number {
  if (
    !Number.isFinite(elevationUpstream) ||
    !Number.isFinite(elevationDownstream) ||
    !Number.isFinite(lengthMeters) ||
    lengthMeters <= MIN_PIPE_LENGTH_M
  ) {
    return 0;
  }
  const dz = elevationDownstream - elevationUpstream;
  const ratio = Math.max(-1, Math.min(1, dz / lengthMeters));
  return Math.asin(ratio);
}

/** Result of {@link computePipeGeometry}. */
export interface PipeGeometry {
  /** Total routed length in metres. */
  lengthMeters: number;
  /** Inclination in radians from horizontal (+uphill along flow). */
  inclinationRad: number;
  /** Upstream (flow inlet) elevation in metres ASL, if known. */
  elevationUpstream?: number;
  /** Downstream (flow outlet) elevation in metres ASL, if known. */
  elevationDownstream?: number;
}

/**
 * Derive full pipe geometry from a routed polyline and optional endpoint
 * elevations. When both elevations are present the inclination is
 * computed; otherwise inclination is 0 (flat — the conservative default
 * matching the legacy hardcoded pipe).
 *
 * @param coords               Routed vertices (≥2). Flow goes
 *                             coords[0] → coords[last].
 * @param elevationUpstream    metres ASL at coords[0]   (optional).
 * @param elevationDownstream  metres ASL at coords[last] (optional).
 */
export function computePipeGeometry(
  coords: readonly LatLng[],
  elevationUpstream?: number,
  elevationDownstream?: number,
): PipeGeometry {
  const lengthMeters = polylineLengthMeters(coords);
  const haveBoth =
    typeof elevationUpstream === 'number' &&
    Number.isFinite(elevationUpstream) &&
    typeof elevationDownstream === 'number' &&
    Number.isFinite(elevationDownstream);
  const inclinationRad = haveBoth
    ? inclinationRadFromElevations(
        elevationUpstream as number,
        elevationDownstream as number,
        lengthMeters,
      )
    : 0;
  return {
    lengthMeters,
    inclinationRad,
    elevationUpstream: haveBoth ? elevationUpstream : undefined,
    elevationDownstream: haveBoth ? elevationDownstream : undefined,
  };
}
